<template>
  <div class="mb-4 flex justify-end py-2">
    <div class="flex justify-between space-x-2">
      <DateFilter
        :model-value="globalQuery"
        :time-period-label="timePeriodLabel"
        :range-type="'custom'"
        @update:model-value="setGlobalQuery"
      />
    </div>
  </div>
  <HeadlessDisclosure
    #default="{ open: isComparisonDrawerOpen }"
  >
    <BaseCard class="mb-4">
      <template v-if="!isComparisonDrawerOpen">
        <template v-if="localQueries.length === 1">
          <div
            class="my-1 flex w-full flex-wrap items-center justify-end space-x-2 border-l-2 border-transparent pl-2 pt-2"
          >
            <div
              class="grid w-full flex-1 grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4"
            >
              <ReportCashierFilter
                v-model="localQueries[0].cashierId"
              />
              <ReportDeviceFilter
                v-model="localQueries[0].deviceId"
              />
              <ReportFilterSelect
                v-if="tenant.isProductsEnabled"
                v-model="localQueries[0].categoryId"
                label="Category"
                :options="categoryOptions"
                class="w-full"
              />
              <ReportFilterSelect
                v-if="tenant.isProductsEnabled"
                v-model="localQueries[0].productTagId"
                label="Product Tag"
                :options="productTagOptions"
                class="w-full"
              />

              <div
                class="col-end-2 flex shrink-0 items-center justify-end space-x-2 sm:col-end-3 md:col-end-5"
              >
                <ButtonExport
                  :is-loading="isExporting"
                  :default-file-export-format="
                    tenant.defaultExportFiletype
                  "
                  @export="
                    (format) =>
                      onExport(apiQueries[0], format)
                  "
                />

                <ButtonWithIcon
                  :disabled="
                    isLocalQueryEmpty(localQueries[0])
                  "
                  @click="removeLocalQuery(0)"
                >
                  <IconX />
                </ButtonWithIcon>
              </div>
            </div>
          </div>
        </template>
      </template>

      <HeadlessDisclosurePanel>
        <template
          v-for="(localQuery, index) in localQueries"
          :key="index"
        >
          <div
            class="my-1 flex w-full flex-wrap items-center justify-end space-x-2 border-l-2 py-2 pl-2"
            :style="{
              borderColor: getColor(
                localQueries[index].colorVariableName
              ),
            }"
          >
            <div
              class="grid w-full min-w-48 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4"
            >
              <ReportCashierFilter
                v-model="localQueries[index].cashierId"
              />
              <ReportDeviceFilter
                v-model="localQueries[index].deviceId"
              />
              <ReportFilterSelect
                v-if="tenant.isProductsEnabled"
                v-model="localQueries[index].categoryId"
                label="Category"
                :options="categoryOptions"
                class="w-full"
              />
              <ReportFilterSelect
                v-if="tenant.isProductsEnabled"
                v-model="localQueries[index].productTagId"
                label="Product Tag"
                :options="productTagOptions"
                class="w-full"
              />
              <div
                class="col-end-2 flex shrink-0 items-center justify-end space-x-2 sm:col-end-3 md:col-end-5"
              >
                <ButtonExport
                  :is-loading="isExporting"
                  :default-file-export-format="
                    tenant.defaultExportFiletype
                  "
                  @export="
                    (format) =>
                      onExport(apiQueries[index], format)
                  "
                />

                <ButtonWithIcon
                  :disabled="
                    localQueries.length === 1 &&
                    isLocalQueryEmpty(localQueries[0])
                  "
                  @click="removeLocalQuery(index)"
                >
                  <IconTrash
                    v-if="localQueries.length > 1"
                  />
                  <IconX v-else />
                </ButtonWithIcon>
              </div>
            </div>
          </div>
        </template>
        <template v-if="localQueries.length < 10">
          <div
            class="flex w-full items-center justify-start space-x-2"
          >
            <button
              :class="[
                'flex items-center justify-center space-x-2 rounded-lg px-3 py-1',
                'hover:bg-background-fg/3',
              ]"
              type="button"
              @click="addLocalQuery"
            >
              <span> Add Dataset </span>
              <IconPlus />
            </button>
          </div>
        </template>
      </HeadlessDisclosurePanel>
      <div class="flex w-full justify-center">
        <HeadlessDisclosureButton
          :class="[
            'flex w-full items-center justify-center rounded-lg py-2',
            'hover:bg-background-fg/3',
          ]"
        >
          <template v-if="!isComparisonDrawerOpen">
            <span> Show Datasets </span>
            <IconChevronDown class="size-6" />
          </template>
          <template v-else>
            <span> Hide </span>
            <IconChevronUp class="size-6" />
          </template>
        </HeadlessDisclosureButton>
      </div>
    </BaseCard>
  </HeadlessDisclosure>

  <BaseCard class="relative">
    <QueryLoading v-if="isLoading" class="min-h-96" />
    <QueryError
      v-else-if="errorInfo.allErrored"
      class="min-h-96"
      :show-retry="true"
      @retry="refetch"
    >
      Failed to retrieve product sales report

      <template v-if="errorInfo.allTimedOut">
        <br />
        <span class="font-normal">
          The request took too long to process, try a
          smaller date range.
        </span>
      </template>
    </QueryError>
    <div v-else class="flex w-full flex-col">
      <div class="flex justify-end py-2">
        <ToggleSwitch
          v-model="graphView"
          :options="graphViewOptions"
          selected-option-color="primary"
          text-size="sm"
        />
      </div>
      <BaseWarningBox
        v-if="errorInfo.hasErrors && !errorInfo.allErrored"
        class="mb-4 p-2"
      >
        <p>
          {{
            errorInfo.multiDatasetSomeTimedout
              ? "Some of the dataset requests took too long to process, try a smaller date range."
              : "Failed to load some of the datasets."
          }}
        </p>
      </BaseWarningBox>
      <div
        class="h-64 w-full overflow-hidden sm:h-96 md:h-[32rem]"
      >
        <template
          v-if="
            datasets.some(
              (dataset) => dataset.data.length > 0
            )
          "
        >
          <template v-if="graphView === 'trend'">
            <ChartBarDate
              id="sales-trend-graph"
              :labels="labels"
              :datasets="datasets"
              :granularity="granularity"
              :currency="tenant.currency"
              @hover-datapoint="handleHoverDatapoint"
            />
          </template>
          <template v-else-if="graphView === 'cumulative'">
            <ChartLineDate
              id="sales-cumulative-graph"
              :labels="labels"
              :datasets="datasets"
              :granularity="granularity"
              is-cumulative
              :currency="tenant.currency"
              @hover-datapoint="handleHoverDatapoint"
            />
          </template>
        </template>
      </div>

      <div
        v-if="!errorInfo.allErrored"
        class="shrink-0 rounded-lg bg-background-fg/5 p-4"
      >
        <template v-if="hoveredData">
          <div class="mb-2">
            <div class="text-sm text-background-fg/70">
              {{ hoveredData.formattedLabel }}
            </div>
          </div>
          <div
            class="grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            <template
              v-for="(
                dataset, index
              ) in hoveredData.datasets"
              :key="dataset.color"
            >
              <div class="flex items-center space-x-4">
                <ColorItem :value="dataset.color" rounded />
                <div
                  class="flex flex-1 flex-wrap items-center justify-start gap-2"
                >
                  <template
                    v-for="filter in getFiltersForIndex(
                      index
                    )"
                    :key="filter"
                  >
                    <span
                      class="rounded-full bg-primary px-3 py-1 text-sm text-primary-fg"
                    >
                      {{ filter }}
                    </span>
                  </template>
                </div>
                <div class="w-36">
                  <p
                    class="text-center text-sm leading-tight text-background-fg/70"
                  >
                    Sales Total
                  </p>
                  <p class="text-center font-semibold">
                    {{
                      formatToMerchantCurrencyString(
                        dataset.value ?? 0
                      )
                    }}
                  </p>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import type { ProductSalesReportQuery } from "@/services/reports/retrieveProductSalesReport"
import type { HoveredDataPoint } from "@/components/ChartLineDate.vue"
import type { Color } from "@/composables/options/useColorOptions"
import type { FileExportFormat } from "@/composables/options/useFileExportFormatOptions"
import type { LocalQuery } from "@/composables/useReportQuery"
import { ApiError } from "@/api"

const { tenant } = useTenant()
const { formatToMerchantCurrencyString } = useCurrency()
const { getLabelForValue: getCashierLabel } =
  useCashierOptions()
const {
  asOptions: categoryOptions,
  getLabelForValue: getCategoryLabel,
} = useCategories()

const {
  asOptions: productTagOptions,
  getById: getProductTagById,
} = useProductTags()
const emptyLocalQuery = {
  cashierId: undefined,
  categoryId: undefined,
}

const isLocalQueryEmpty = (
  query: LocalQuery<ProductSalesReportQuery>
) =>
  query.cashierId === emptyLocalQuery.cashierId &&
  query.categoryId === emptyLocalQuery.categoryId &&
  !query.deviceId &&
  !query.productTagId

const getColor = (colorVariableName: Color | undefined) =>
  colorVariableName
    ? `rgb(var(${colorVariableName}))`
    : undefined

const {
  apiQueries,
  localQueries,
  addLocalQuery,
  removeLocalQuery,
  globalQuery,
  setGlobalQuery,
  timePeriodLabel,
} = useReportQuery<ProductSalesReportQuery>(emptyLocalQuery)

const { queryResults, errors, refetch, isLoading } =
  useProductSalesReports(apiQueries)

const errorInfo = computed(() => {
  const allTimedOut = errors.value.every(
    (error) =>
      error instanceof ApiError &&
      error.code === "database-timeout"
  )
  const multiDatasetSomeTimedout =
    queryResults.length > 1 &&
    errors.value.some(
      (error) =>
        error instanceof ApiError &&
        error.code === "database-timeout"
    )
  const hasErrors = errors.value.some(
    (error) => error !== null
  )
  const allErrored = errors.value.every(
    (error) => error !== null
  )
  return {
    allTimedOut,
    multiDatasetSomeTimedout,
    hasErrors,
    allErrored,
  }
})

const rawDatasets = computed(() =>
  queryResults.map(({ data }) => data?.dataset)
)

const granularity = computed(
  () => queryResults?.[0]?.data?.granularity
)

const labels = computed(() => {
  const firstNonNullRawDataset = rawDatasets.value.find(
    (rawDataset) =>
      Array.isArray(rawDataset) && rawDataset?.length > 0
  )
  return (
    firstNonNullRawDataset?.map(({ start, end }) => ({
      start,
      end,
    })) || []
  )
})

const datasets = computed(() =>
  rawDatasets.value.map((rawDataset, index) => {
    const colorVariableName =
      localQueries[index].colorVariableName
    const colorVariable = colorVariableName
      ? useCssVar(colorVariableName).value
      : undefined
    const color = colorVariable
      ? `rgb(${colorVariable})`
      : undefined
    return {
      label: "",
      data: rawDataset?.map(({ value }) => value) || [],
      color: color,
    }
  })
)

const handleHoverDatapoint = (
  hoveredData_: HoveredDataPoint
) => {
  hoveredData.value = hoveredData_
}

const hoveredData = ref<HoveredDataPoint>()

const graphView = ref<"trend" | "cumulative">("trend")
const graphViewOptions = [
  { label: "Trend", value: "trend" },
  { label: "Cumulative", value: "cumulative" },
] as const

const isExporting = ref(false)
const onExport = async (
  query: ProductSalesReportQuery,
  format?: FileExportFormat
) => {
  if (isExporting.value) return
  isExporting.value = true
  try {
    await exportProductSalesReport(query, format)
  } catch {
    toast.error("Failed to export product sales")
  } finally {
    isExporting.value = false
  }
}

const getFiltersForIndex = (index: number) => {
  const localQuery_ = localQueries[index]
  if (!localQuery_) return
  return [
    ...(localQuery_.cashierId
      ? [getCashierLabel(localQuery_.cashierId).value]
      : []),
    ...(localQuery_.categoryId
      ? [getCategoryLabel(localQuery_.categoryId).value]
      : []),
    ...(localQuery_.productTagId
      ? [
          getProductTagById(localQuery_.productTagId).value
            ?.label,
        ]
      : []),
  ]
}
</script>
